import { Component, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';

interface ProjectLink {
  label: string;
  url: string;
}

interface Project {
  title: string;
  description: string;
  url?: string;
  links?: ProjectLink[];
  tags: string[];
}

@Component({
  selector: 'app-homepage',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './homepage.component.html',
  styleUrls: ['./homepage.component.scss'],
})
export class HomepageComponent {
  private router = inject(Router);

  projects: Project[] = [
    {
      title: '🇦🇹 Key Web Platforms for the City of Vienna',
      description:
        'Contributed to the development and/or maintenance of several significant Viennese websites, including the official Wien Holding portal, the City of Vienna\'s Infodat service, the Donauinselfest website and the Vorteilsclub Wien.',
      links: [
        { label: 'Wien Holding', url: 'https://www.wienholding.at' },
        { label: 'Wien.gv.at Infodat', url: 'https://www.wien.gv.at/infodat/' },
        { label: 'Donauinselfest', url: 'https://donauinselfest.at' },
        { label: 'Vorteilsclub', url: 'https://vorteilsclub.wien.at/' },
        { label: 'Wien Holding Sport', url: 'https://www.wh-sport.at' },
        { label: 'Messe Congress', url: 'https://www.messecongress.at' },
        { label: 'Viennabase', url: 'https://viennabase.at/' },
        {
          label: 'Mauthausen Memorial',
          url: 'https://www.mauthausen-memorial.org',
        },
        { label: 'Gusen Memorial', url: 'https://www.gusen-memorial.org' },
        {
          label: 'Wien Holding Digital Services',
          url: 'https://www.wh-d.at',
        },
        { label: 'Wien Holding Media', url: 'https://www.wh-m.at' },
      ],
      tags: [
        'React',
        'TypeScript',
        'Java',
        'Spring Boot',
        'WordPress',
        'PHP',
        'FreeMarker',
        'CraftCMS',
      ],
    },
    {
      title: '🌐 Sandclock DeFi Application',
      description:
        'Contributed to the development and launch of two versions of a decentralized finance (DeFi) application. Developed scalable front-end components using React and TypeScript, and integrated blockchain interactions using Viem, Wagmi, and Ethers.js.',
      url: 'https://app.sandclock.org/',
      tags: [
        'React',
        'TypeScript',
        'Next.js',
        'Zustand',
        'Tanstack Query',
        'GraphQL',
        'TailwindCSS',
        'Shadcn/UI',
        'Viem',
        'Wagmi',
        'Ethers.js',
        'Supabase',
      ],
    },
    {
      title: '🎨 Tiny React Material Design 3 (TRMD3)',
      description:
        'Developed and maintained TRMD3, a high-performance and customizable React component library adhering to Material Design 3 principles. Published and maintained the npm package "trmd3components".',
      url: 'https://trmd3.com/',
      links: [
        {
          label: 'npm: trmd3components',
          url: 'https://www.npmjs.com/package/trmd3components',
        },
      ],
      tags: ['React', 'TypeScript', 'npm', 'MD3'],
    },
    {
      title: '🎰 Lucky Slots Web',
      description:
        'A provably fair online slot machine engine (V3 rewrite) built as a full-stack monorepo. Features a cryptographically verifiable HMAC-SHA256 spin system, a two-phase simulated annealing reel optimizer targeting custom RTP and hit-rate goals, and a Monte Carlo RTP simulator. The isomorphic TypeScript engine powers both the Nuxt 3 frontend and the GraphQL API.',
      url: 'https://lucky-slots-web.fly.dev/',
      links: [
        { label: 'GitHub', url: 'https://github.com/highnet/lucky-slots-v3' },
      ],
      tags: [
        'Nuxt 3',
        'Node.js',
        'TypeScript',
        'GraphQL Yoga',
        'Drizzle ORM',
        'PostgreSQL',
        'Redis',
        'XState',
        'TresJS',
        'Pinia',
        'TailwindCSS',
        'Turborepo',
        'Docker',
        'Fly.io',
      ],
    },
    {
      title: '🃏 War',
      description:
        'A fullstack monorepo implementation of the classic card game War, featuring real-time multiplayer, AI opponents, and Redis-backed game state.',
      links: [
        { label: 'GitHub', url: 'https://github.com/highnet/war' },
        {
          label: 'Live App',
          url: 'https://highnet-war-frontend.fly.dev/lobby',
        },
      ],
      tags: ['React', 'TypeScript', 'Nuxt', 'Node.js', 'Redis', 'WebSockets', 'Fly.io'],
    },
    {
      title: '📖 Convex Guestbook',
      description:
        'A member-only guestbook where authenticated users can sign in, post messages, and watch the list update in real time. Built on the Next.js App Router with Convex as the reactive backend, Clerk for auth, and PostHog for product analytics.',
      links: [
        {
          label: 'GitHub',
          url: 'https://github.com/highnet/convex-guestbook',
        },
        {
          label: 'Live App',
          url: 'https://convex-guestbook.vercel.app/',
        },
      ],
      tags: ['Next.js', 'Convex', 'Clerk', 'PostHog', 'TypeScript', 'Vercel'],
    },
    {
      title: '🛰️ Orbital',
      description:
        'A real-time satellite viewer that ingests live orbital data, normalizes it into clean snapshots, and renders active satellites on an interactive 3D globe.',
      url: 'https://github.com/highnet/orbital',
      tags: ['React', 'Three.js', 'WebGL'],
    },
    {
      title: '🤖 Ollama ZeroClaw',
      description:
        'A local AI agent setup backed by Ollama, with scripts for both Windows and WSL2 that spin up a fully terminal-based UI (TUI) — no cloud, no browser required.',
      url: 'https://github.com/highnet/ollama-zeroclaw',
      tags: ['Ollama', 'Bash/PowerShell', 'TUI'],
    },
    {
      title: '⚔️ AI Arena',
      description:
        'Pits AI-controlled battle agents against each other in strategic turn-based combat. Each agent has unique stats, moves, and a personality that shapes its decision-making, powered by a local LLM running entirely on your machine.',
      url: 'https://github.com/highnet/ai-arena',
      tags: ['Python', 'LLM', 'AI'],
    },
    {
      title: '🎮 Retro Karting 22',
      description:
        'A single-player, time trial-based karting game focused on reaction time and driving skills. Brings classic kart racing fun to Steam.',
      url: 'https://store.steampowered.com/app/1906070/Retro_Karting_22/',
      tags: ['Unity', 'C#', 'Steam'],
    },
    {
      title: '♟️ Idle Chess Story',
      description:
        'A strategic idle game where players assemble chess units with unique abilities to compete in tournaments.',
      url: 'https://store.steampowered.com/app/1067110/Idle_Chess_Story/',
      tags: ['Unity', 'C#', 'Steam'],
    },
  ];

  goToCV(): void {
    this.router.navigate(['/about']);
  }
}
